'use client';

import { useMemo } from 'react';
import type {
  ProductInner,
  ProductLandingPageType,
  ProductRelationComponent,
} from '@/types/product';
import { localizeUrl, stripHtml } from '@/lib/homepage-mappers';
import { resolveStrapiMediaUrl } from '@/lib/strapi-media-url';
import { normalizeProductSlug } from '@/lib/product-slug';
import { getCmsButtonTarget } from '@/lib/cms-button-target';
import ProductTypeCard, { type ProductTypeCardData } from './ProductTypeCard';

const PLACEHOLDER_IMAGE = '/images/product_img1.png';

type MaybeAsset = Record<string, unknown> | null | undefined;

function pickFirstString(...candidates: Array<unknown>): string | undefined {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) return c.trim();
  }
  return undefined;
}

function isImageAsset(asset: MaybeAsset): boolean {
  if (!asset) return false;
  const mime = pickFirstString(asset.mime);
  if (mime) return mime.startsWith('image/');
  const url = pickFirstString(asset.url);
  if (!url) return false;
  return !/\.(mp4|webm|mov)(\?|$)/i.test(url);
}

function urlFromStrapiAsset(asset: MaybeAsset): string | undefined {
  if (!asset || typeof asset !== 'object') return undefined;
  return pickFirstString(asset.url);
}

/** Pick the right image: mediaPoster first (always an image), then media if it's an image. */
function extractProductCardImageRaw(card: ProductInner['productCard']): string | undefined {
  const poster = card.mediaPoster as unknown as MaybeAsset;
  const fromPoster = urlFromStrapiAsset(poster);
  if (fromPoster) return fromPoster;

  const media = card.media as unknown as MaybeAsset;
  if (isImageAsset(media)) {
    const fromMedia = urlFromStrapiAsset(media);
    if (fromMedia) return fromMedia;
  }
  return undefined;
}

function productCardImageSrc(card: ProductInner['productCard']): string {
  const raw = extractProductCardImageRaw(card);
  return (raw ? resolveStrapiMediaUrl(raw) : null) || PLACEHOLDER_IMAGE;
}

function mapApiToCards(component: ProductRelationComponent, locale: string): ProductTypeCardData[] {
  return component.product_inners.map((inner) => {
    const card = inner.productCard;
    const imageUrl = productCardImageSrc(card);
    const bullets = card.list?.length
      ? card.list.map((item) => stripHtml(item.listContent || item.listTitle || '')).filter(Boolean)
      : [];

    let buttonLight: NonNullable<typeof card.buttonLight>;
    if (Array.isArray(card.buttonLight)) {
      buttonLight = card.buttonLight;
    } else if (card.buttonLight) {
      buttonLight = [card.buttonLight];
    } else {
      buttonLight = [];
    }
    const primaryBtn = buttonLight.find((btn) => btn.buttonType === 'Solid') || buttonLight[0];

    const productSlug = normalizeProductSlug(inner.productSlug);
    const learnMoreHref = productSlug ? `/${locale}/products/${productSlug}` : undefined;
    const primaryUrl = localizeUrl(primaryBtn?.buttonUrl, locale) ?? undefined;

    return {
      id: String(inner.id),
      title: card.heading || inner.productTitle,
      tagline: inner.productSubtitle || '',
      description: stripHtml(card.subHeading || ''),
      imageSrc: imageUrl,
      imageAlt:
        (card.media as { alternativeText?: string } | undefined)?.alternativeText ||
        card.heading ||
        inner.productTitle,
      listgroupTitle: card.listgroupTitle?.trim() || '',
      bestFor: bullets,
      primaryCtaText: primaryBtn?.buttonText ?? '',
      primaryCtaHref: primaryUrl,
      primaryCtaTarget: getCmsButtonTarget(primaryBtn),
      learnMoreHref,
    };
  });
}

type ProductTypesSectionProps = {
  locale: string;
  productLandingData?: ProductLandingPageType | null;
  relation?: ProductRelationComponent;
};

export default function ProductTypesSection({
  locale,
  productLandingData,
  relation,
}: ProductTypesSectionProps) {
  const resolvedRelation =
    relation ??
    productLandingData?.components?.find(
      (c): c is ProductRelationComponent => c.__component === 'products.product-relation',
    );

  const sectionTitle = resolvedRelation?.sectionTitle?.trim();
  const items = useMemo(() => {
    if (!resolvedRelation?.product_inners?.length) return [];
    return mapApiToCards(resolvedRelation, locale);
  }, [resolvedRelation, locale]);

  if (!items.length) return null;

  return (
    <section className="bg-black text-white max-sm:!px-4" aria-labelledby="product-types-heading">
      <div className="container mx-auto">
        {sectionTitle ? (
          <h2
            id="product-types-heading"
            className="mx-auto mb-8 max-w-4xl text-center text-3xl font-bold leading-tight tracking-tight text-white md:mb-10 md:text-4xl lg:text-[2.5rem]"
          >
            {sectionTitle}
          </h2>
        ) : null}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {items.map((item) => (
            <ProductTypeCard key={item.id} {...item} />
          ))}
        </div>
      </div>
    </section>
  );
}
